import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { BedDouble, Images, PenLine, MessageCircle, AlertTriangle, ChevronRight, Search } from "lucide-react";
import { api } from "../../lib/api";
import { PageTitle } from "../../components/admin/Field";

const Stat = ({ label, value, sub, testid }) => (
  <div data-testid={testid} className="bg-[#0A0A0A] border border-zinc-800/80 rounded-lg p-5">
    <p className="text-xs uppercase tracking-widest text-zinc-500">{label}</p>
    <p className="font-display text-5xl text-[#E6C687] mt-2">{value}</p>
    {sub && <p className="text-xs text-zinc-500 mt-1">{sub}</p>}
  </div>
);

const Shortcut = ({ to, icon: Icon, label, testid }) => (
  <Link to={to} data-testid={testid} className="flex items-center gap-4 p-5 min-h-[64px] bg-[#0A0A0A] border border-zinc-800/80 rounded-lg hover:border-[#D4AF37]/50 transition-colors">
    <span className="h-11 w-11 grid place-items-center rounded-full bg-[#D4AF37]/10 text-[#D4AF37]"><Icon size={20} /></span>
    <span className="flex-1 font-medium">{label}</span>
    <ChevronRight size={18} className="text-zinc-600" />
  </Link>
);

const fmt = (iso) => new Date(iso).toLocaleString("pt-BR", { day: "2-digit", month: "2-digit", hour: "2-digit", minute: "2-digit" });

export default function Dashboard() {
  const [s, setS] = useState(null);
  useEffect(() => {
    api.get("/admin/stats").then((r) => setS(r.data));
  }, []);
  if (!s) return <p className="text-zinc-500">Carregando…</p>;

  const alerts = [
    !s.whatsapp_configured && ["Configure o número de WhatsApp para ativar os botões de reserva.", "/admin/conteudo?aba=whatsapp", "alert-whatsapp"],
    s.has_unpublished && ["Há alterações de conteúdo salvas e ainda não publicadas.", "/admin/conteudo", "alert-unpublished"],
    s.photos_samples > 0 && [`${s.photos_samples} foto(s) de exemplo ainda no site. Substitua pelas fotos reais.`, "/admin/fotos", "alert-samples"],
  ].filter(Boolean);

  return (
    <div data-testid="admin-dashboard">
      <PageTitle title="Bem-vindo" desc="Gerencie o site do Vienna Palace Hotel." />
      {alerts.length > 0 && (
        <div className="space-y-3 mb-8">
          {alerts.map(([t, to, id]) => (
            <Link key={id} to={to} data-testid={id} className="flex items-start gap-3 p-4 rounded-lg border border-[#D4AF37]/30 bg-[#D4AF37]/5 text-sm text-[#E6C687]">
              <AlertTriangle size={18} className="shrink-0 mt-0.5" /> <span className="flex-1">{t}</span> <ChevronRight size={16} />
            </Link>
          ))}
        </div>
      )}
      <div className="grid grid-cols-2 lg:grid-cols-3 gap-3 sm:gap-4">
        <Stat label="Acomodações" value={s.accommodations} sub={`${s.accommodations_published} publicada(s)`} testid="stat-accommodations" />
        <Stat label="Fotos publicadas" value={s.photos_published} testid="stat-photos" />
        <Stat label="WhatsApp" value={s.whatsapp_configured ? "Ativo" : "—"} sub={s.whatsapp_configured ? "botões funcionando" : "não configurado"} testid="stat-whatsapp" />
      </div>
      <div className="grid lg:grid-cols-2 gap-8 mt-10">
        <div className="space-y-3">
          <h2 className="font-display text-2xl mb-4">Atalhos</h2>
          <Shortcut to="/admin/fotos" icon={Images} label="Adicionar ou editar fotos" testid="shortcut-photos" />
          <Shortcut to="/admin/acomodacoes" icon={BedDouble} label="Gerenciar acomodações" testid="shortcut-rooms" />
          <Shortcut to="/admin/conteudo" icon={PenLine} label="Editar textos e contatos" testid="shortcut-content" />
          <Shortcut to="/admin/conteudo?aba=whatsapp" icon={MessageCircle} label="WhatsApp e mensagens" testid="shortcut-whatsapp" />
          <Shortcut to="/admin/conteudo?aba=seo" icon={Search} label="SEO (Google)" testid="shortcut-seo" />
        </div>
        <div>
          <h2 className="font-display text-2xl mb-4">Últimas alterações</h2>
          <ul className="bg-[#0A0A0A] border border-zinc-800/80 rounded-lg divide-y divide-zinc-800/80" data-testid="activity-list">
            {s.activity.length === 0 && <li className="p-4 text-sm text-zinc-500">Nenhuma alteração ainda.</li>}
            {s.activity.map((a) => (
              <li key={a.id} className="p-4 flex justify-between gap-4 text-sm">
                <span><span className="text-zinc-200">{a.action}</span>{a.detail && <span className="text-zinc-500"> — {a.detail}</span>}</span>
                <span className="text-zinc-500 shrink-0 text-xs">{fmt(a.at)}</span>
              </li>
            ))}
          </ul>
        </div>
      </div>
    </div>
  );
}
